import { useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { clsx } from 'clsx';
import { Outlet, useLocation, useMatches } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';

import { BottomNav, NavRail } from './BottomNav';
import { AppHeader } from './AppHeader';
import { MeQuery } from './guards';
import { pageWidthClass, widthOf } from './pageWidth';
import { LiveSessionContext, type LiveSession } from '../session/live';
import { registerFor } from '../profile/register';
import { useLocale } from '../i18n/LocaleProvider';
import { isShipped } from '../i18n/locale';
import { t } from '../i18n/t';
import { UpdateNotice } from '../update/UpdateNotice';

import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

/**
 * Sets `data-theme="kid"` on the document root, or removes it for adult —
 * §9.3's register, read the same way `main.tsx` read it before the router:
 * `app.css` keys the kid palette, the avatar's motion intensity and the
 * 64px `--hit-min` hit targets off this one attribute.
 *
 * Its own component, suspended in its own `Suspense`, rather than a
 * `useLazyLoadQuery` call inside `AppLayout` itself: `AppLayout` has no
 * boundary above it, so a suspend there would blank the nav along with
 * everything else while this query resolves — the exact symptom the
 * per-route `Suspense` around `Outlet` exists to avoid. `fallback={null}`:
 * there is nothing to show meanwhile, so the attribute just lands a beat
 * after the chrome around it.
 *
 * Reuses `guards.tsx`'s query rather than declaring a second one: it is the
 * same `me` shape `RequireAccount` and `RequireCharacter` already read, so
 * whichever of the three fetches first satisfies the rest from the store.
 */
function ThemeAttribute() {
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const register = registerFor(data.me?.learner?.defaultCharacter?.audience);
  if (register === 'kid') {
    document.documentElement.setAttribute('data-theme', 'kid');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
  return null;
}

/**
 * Puts the page in the learner's own interface locale once `me` answers
 * (spec §3.2): the server decides it, from their choice or their native
 * language, and this never re-derives it. Beside `ThemeAttribute`, for the
 * same reasons.
 *
 * The switch remounts the tree under `LocaleProvider`, this component with
 * it; the second mount finds the locale already right and does nothing.
 * `qps` is left alone, since it outranks any learner's choice.
 */
function InterfaceLocale() {
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const wanted = data.me?.learner?.interfaceLocale;
  const { locale, choose } = useLocale();
  useEffect(() => {
    if (wanted && isShipped(wanted) && wanted !== locale && locale !== 'qps') void choose(wanted);
  }, [wanted, locale, choose]);
  return null;
}

/**
 * The shell, mounted once.
 *
 * It is a layout route rather than a wrapper each screen renders, and that is
 * the whole point of this change: the nav is mounted by the parent route and
 * does not re-mount when the child changes. Five copies of it used to live in
 * `main.tsx`'s switch, and every screen change tore the whole tree down.
 *
 * The Suspense boundary is here, inside the shell, rather than around it. A
 * boundary above the nav would blank the nav along with the panel every time
 * a Relay query suspended, which is the same symptom this removes rather than
 * a fix for it.
 *
 * Neither nav is fixed and no screen reserves space for one. They are rows of
 * a viewport-filling column whose middle scrolls, so nothing can sit underneath them --
 * which is what `pb-24` used to be for, on every screen, as a standing tax for
 * an overlay that no longer exists.
 *
 * All three rows centre their contents on the same `max-w-2xl`, and none of
 * them *is* that column: the header, the scroller and the nav each span the
 * window and hold a centred child. The scroller has to, or its scrollbar is
 * drawn at the right edge of the reading column -- a bar hanging in the middle
 * of a wide window, nowhere near the edge every other page puts it. The other
 * two follow so the logo, the content and the tabs share one left margin.
 *
 * From `md` up the tabs are `NavRail`, a column beside the scroller, and the
 * header spans the window instead: there the reading column is centred in
 * what the rail leaves, and only the scroller holds to `max-w-2xl`.
 *
 * Amendment (large-desktop plan, 2026-11-02): the scroller's column is now
 * as wide as the route's `handle` says (`pageWidth.ts`). A reading screen
 * keeps `max-w-2xl`; a wide one grows from `lg`, where a 672 px column left
 * ~580 px empty on each side of a 1920 px window.
 */
export function AppLayout() {
  // Held by the shell rather than by the session screen, because the navs
  // that need it outlive that screen.
  const [live, setLive] = useState<LiveSession>({
    live: false,
    end: async () => {},
  });
  const value = useMemo(() => ({ ...live, set: setLive }), [live]);
  const width = widthOf(useMatches().map((m) => m.handle));

  // `main` is one scroller for every screen, so without this a screen opened
  // from the bottom of a long one opened at that offset. Back included: left
  // alone, Back did not restore the screen's own place but kept the offset of
  // the screen being left, clamped. Restoring it would need the offset saved
  // per history entry and re-applied after the screen's queries resolve; the
  // top is what every other way in gives.
  //
  // `scrollTop`, not `scrollTo()`: the bundle smoke tests' DOM has no
  // `Element.scrollTo`, and the throw took the whole shell down.
  const scroller = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [pathname]);

  return (
    <LiveSessionContext.Provider value={value}>
      <Suspense fallback={null}>
        <ThemeAttribute />
        <InterfaceLocale />
      </Suspense>
      {/* A fixed app shell, not a scrolling document.
          The whole app used to scroll as one page: the header went off the top
          with everything else, and the conversation screen's `sticky` avatar
          never engaged, because `sticky` needs its scroll container to be an
          ancestor and the scroller was the document while `main` was just a
          tall block inside it. Here `main` is the only scroller, so the nav
          above and below it stay put and `sticky` inside it works.
          `h-dvh`, not `h-screen`: on a phone `100vh` measures the viewport
          with the browser chrome *hidden*, so a `h-screen` shell is taller
          than what is visible and its bottom row hides under the URL bar until
          you scroll. `dvh` tracks that chrome as it collapses.
          `min-h-0` on `main` because a flex item's default `min-height: auto`
          refuses to shrink below its content -- without it `overflow-y-auto`
          never has an overflow to handle and the shell grows past the
          viewport instead.
          `relative` on `main`: an `absolute` descendant (every `sr-only`
          label) is placed against its nearest positioned ancestor, so
          without it the lesson library's labels escaped `main` and made the
          document taller than the shell, scrolling the nav off its place. */}
      <div className="flex h-dvh flex-col">
        <AppHeader />
        {/* `scrollbar-gutter: stable` so arriving at a screen long enough to
            scroll does not shove the column sideways by the width of the bar.
            The inner div is the reading column; `h-full` on it is what lets a
            screen fill the viewport exactly rather than scroll -- the
            conversation does that, and its own bottom row is why. */}
        <div className="flex min-h-0 flex-1">
          <NavRail />
          <main
            ref={scroller}
            className="relative min-h-0 w-full flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
          >
            <div className={clsx('mx-auto h-full w-full px-4', pageWidthClass(width))}>
              <Suspense
                fallback={
                  <p role="status" className="text-base-content/60 py-8 text-sm">
                    {t('common-loading')}
                  </p>
                }
              >
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
        <UpdateNotice />
        <BottomNav />
      </div>
    </LiveSessionContext.Provider>
  );
}
